import { apiClient } from '@/lib/api/client'

export interface DashboardStats {
  total_users: number
  total_media: number
  total_categories: number
  total_tags: number
}

export interface EnhancedDashboardStats extends DashboardStats {
  active_users: number
  new_users_today: number
  new_users_this_week: number
  new_users_this_month: number
  users_last_30_days: number
  total_pages: number
  published_pages: number
  draft_pages: number
  pages_this_month: number
  media_this_month: number
  activities_today: number
  activities_this_week: number
}

export interface DashboardWidget {
  id: number
  user_id: number | null
  widget_type: 'metric' | 'line_chart' | 'bar_chart' | 'pie_chart' | 'table'
  title: string
  data_source: string
  query_config: Record<string, any>
  visualization_config: Record<string, any>
  position_x: number
  position_y: number
  width: number
  height: number
  order_index: number
  active: boolean
  created_at: string
  updated_at: string
}

export interface ChartData {
  labels: string[]
  data: number[]
}

export interface AuthDashboardStats {
  clusters: {
    total: number
    active: number
    inactive: number
    deleted: number
    by_parent: Array<{ parent_id: number | null; count: number }>
  }
  clients: {
    total: number
    active: number
    inactive: number
    deleted: number
    new_today: number
    new_this_week: number
    new_this_month: number
  }
  field_engineers: {
    total: number
    active: number
    inactive: number
    deleted: number
    new_today: number
    new_this_week: number
    new_this_month: number
    by_cluster: Array<{ cluster: string; count: number }>
    by_region: Array<{ region: string; count: number }>
  }
  users: {
    total: number
    active: number
    inactive: number
    deleted: number
    new_today: number
    new_this_week: number
    new_this_month: number
    new_last_30_days: number
    active_last_30_days: number
    by_role: Array<{ role: string; count: number }>
  }
  assigned_devices: {
    total: number
    active: number
    inactive: number
    deleted: number
    new_today: number
    new_this_week: number
    new_this_month: number
    by_user: Array<{ user_id: number; user_name: string; count: number }>
    by_status: Array<{ status: string; count: number }>
  }
  system_settings: {
    total: number
    key_settings: Array<{
      key: string
      value: string
      type: string
      description: string | null
    }>
    by_category: {
      security: Array<{
        key: string
        value: string
        type: string
        description: string | null
      }>
      email: Array<{
        key: string
        value: string
        type: string
        description: string | null
      }>
      general: Array<{
        key: string
        value: string
        type: string
        description: string | null
      }>
    }
  }
  security: {
    failed_logins_24h: number
    failed_logins_yesterday: number
    active_sessions: number
    locked_users: number
    disabled_users: number
    status: 'green' | 'yellow' | 'red'
    trend: 'increasing' | 'decreasing' | 'stable'
  }
  clients_summary: Array<{
    id: number
    name: string
    code: string
    status: boolean
    users_count: number
    field_engineers_count: number
    assigned_devices_count: number
    last_activity: string | null
    created_at: string | null
  }>
  activity_trends: Array<{
    date: string
    label: string
    count: number
  }>
  user_registration_trends: Array<{
    date: string
    label: string
    count: number
  }>
  hourly_trends: {
    today: number[]
    yesterday: number[]
  }
  monthly_module_activities_trends: {
    trends: Array<{
      date: string
      label: string
      total: number
      by_module: Record<string, number>
    }>
    per_module: Array<{
      module: string
      total: number
      daily_trends: Array<{
        date: string
        label: string
        count: number
      }>
    }>
    top_modules: string[]
    module_totals: Record<string, number>
  }
  module_analytics: {
    per_module: Array<{
      module: string
      today: number
      this_week: number
      this_month: number
    }>
    total_today: number
    total_this_week: number
    total_this_month: number
  }
  roles: {
    total: number
    active: number
    inactive: number
    deleted: number
    by_role: Array<{ role: string; count: number }>
  }
  teams: {
    total: number
    active: number
    inactive: number
    deleted: number
    teams_with_members: Array<{ team_id: number; name: string; members_count: number }>
  }
  applications: {
    total: number
    active: number
    inactive: number
    deleted: number
  }
  recent_users: Array<{
    id: number
    name: string
    user_login: string
    user_email: string
    user_status: boolean
    created_at: string | null
  }>
}

export interface AuditTrailDashboardData {
  today: {
    total_actions: number
    actions_by_module: Record<string, number>
    actions_by_type: Record<string, number>
    top_users: Array<{ user_id: number; user_name: string; action_count: number }>
  }
  yesterday: {
    total_actions: number
    actions_by_module: Record<string, number>
    actions_by_type: Record<string, number>
  }
  this_week: {
    total_actions: number
    actions_by_module: Record<string, number>
    actions_by_type: Record<string, number>
  }
  available_files: number
  latest_log_file: string | null
}

export const dashboardService = {
  /**
   * Get basic dashboard statistics (SIMA dashboard)
   */
  getStats: async (): Promise<DashboardStats> => {
    try {
      const response = await apiClient.get('/dashboard/stats')
      return response.data.data || response.data
    } catch (error) {
      console.warn('Dashboard stats endpoint not available:', error)
      return {
        total_users: 0,
        total_media: 0,
        total_categories: 0,
        total_tags: 0,
      }
    }
  },

  /**
   * Get Auth application dashboard statistics
   */
  getAuthStats: async (): Promise<AuthDashboardStats | null> => {
    try {
      const response = await apiClient.get('/dashboard/auth/stats')
      return response.data.data || response.data
    } catch (error) {
      console.warn('Auth dashboard stats endpoint not available:', error)
      return null
    }
  },

  /**
   * Get enhanced dashboard statistics
   */
  getEnhancedStats: async (): Promise<EnhancedDashboardStats> => {
    try {
      const response = await apiClient.get('/dashboard/enhanced-stats')
      return response.data.data || response.data
    } catch (error) {
      console.warn('Enhanced stats endpoint not available:', error)
      throw error
    }
  },

  /**
   * Get audit trail dashboard data
   */
  getAuditTrailDashboard: async (): Promise<AuditTrailDashboardData | null> => {
    try {
      const response = await apiClient.get('/audit-trail/dashboard')
      return response.data.dashboard || response.data.data
    } catch (error) {
      console.warn('Audit trail dashboard not available:', error)
      return null
    }
  },

  /**
   * Get audit trail statistics for a specific date
   */
  getAuditTrailStatistics: async (date?: string): Promise<any> => {
    try {
      const response = await apiClient.get('/audit-trail/statistics', {
        params: { date: date || new Date().toISOString().split('T')[0] },
      })
      return response.data.statistics || response.data.data
    } catch (error) {
      console.warn('Audit trail statistics not available:', error)
      return null
    }
  },

  /**
   * Get all dashboard widgets
   */
  getWidgets: async (): Promise<DashboardWidget[]> => {
    try {
      const response = await apiClient.get('/dashboard/widgets')
      return response.data.data || []
    } catch (error) {
      console.warn('Dashboard widgets not available:', error)
      return []
    }
  },

  /**
   * Get widget data
   */
  getWidgetData: async (widgetId: number): Promise<any> => {
    const response = await apiClient.get(`/dashboard/widgets/${widgetId}/data`)
    return response.data.data
  },

  /**
   * Get user registration trend
   */
  getUserRegistrationTrend: async (
    period: 'daily' | 'weekly' | 'monthly' = 'daily',
    days: number = 30
  ): Promise<ChartData> => {
    try {
      const response = await apiClient.get('/dashboard/user-registration-trend', {
        params: { period, days },
      })
      return response.data.data
    } catch (error) {
      console.warn('User registration trend not available:', error)
      return { labels: [], data: [] }
    }
  },

  /**
   * Get pages by status
   */
  getPagesByStatus: async (): Promise<ChartData> => {
    try {
      const response = await apiClient.get('/dashboard/pages-by-status')
      return response.data.data
    } catch (error) {
      console.warn('Pages by status not available:', error)
      return { labels: [], data: [] }
    }
  },
}

